import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader, AlertTriangle, Calendar, Clock, MapPin, User, Briefcase, Wrench } from 'lucide-react';
import { toast } from 'sonner';
// 🆕 Phase C6 — strip HTML brut dans les titres de BT (ex: "<h3><strong>…")
import { stripHtml } from '@/lib/stripHtml';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * 🆕 2026-05-14 — Popup d'horaire détaillé d'un employé pour une date.
 * Ouvert depuis la bannière de conflit quand on clique sur un prénom :
 * permet au planificateur de voir QUI fait QUOI sans ouvrir chaque BT.
 */
function HoraireEmployeJourDialog({ open, onClose, employeeId, employeePrenom, date }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // 🆕 2026-05-14 — Swap rapide : { btId, btLabel } | null
  const [swapDialog, setSwapDialog] = useState(null);
  const [swapCandidates, setSwapCandidates] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapTargetEmp, setSwapTargetEmp] = useState(""); // "btId|empId"
  const [swapping, setSwapping] = useState(false);
  // 🆕 2026-05-14 — Move (déplacement simple sans échange)
  const [moveDialog, setMoveDialog] = useState(null);
  const [moveCandidates, setMoveCandidates] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveTargetBt, setMoveTargetBt] = useState("");
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!open || !employeeId || !date) return;
    setLoading(true);
    axios.get(`${API_URL}/api/crm/employe/${employeeId}/horaire-jour`, { params: { date } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, employeeId, date]);

  // Ouvrir le picker de swap pour un BT
  const openSwap = async (btId, btLabel) => {
    setSwapDialog({ btId, btLabel });
    setSwapTargetEmp("");
    setSwapLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/crm/bons-travail/jour/${date}/employes`, {
        params: { exclude_bt_id: btId, exclude_employee_id: employeeId },
      });
      setSwapCandidates(r.data?.bts || []);
    } catch (_e) {
      setSwapCandidates([]);
    } finally {
      setSwapLoading(false);
    }
  };

  const doSwap = async () => {
    if (!swapDialog || !swapTargetEmp) return;
    const [targetBtId, targetEmpId] = swapTargetEmp.split("|");
    setSwapping(true);
    try {
      await axios.post(`${API_URL}/api/crm/bons-travail/swap-employee-day`, {
        bt_id_source: swapDialog.btId,
        bt_id_target: targetBtId,
        employee_id_source: employeeId,
        employee_id_target: targetEmpId,
        date,
      });
      toast.success("Échange effectué ✓");
      // Rafraîchir l'horaire
      const r = await axios.get(`${API_URL}/api/crm/employe/${employeeId}/horaire-jour`, { params: { date } });
      setData(r.data);
      setSwapDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'échange");
    } finally {
      setSwapping(false);
    }
  };

  // Ouvrir le picker de déplacement (sans échange) pour un BT
  const openMove = async (btId, btLabel) => {
    setMoveDialog({ btId, btLabel });
    setMoveTargetBt("");
    setMoveLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/crm/bons-travail/jour/${date}/employes`, {
        params: { exclude_bt_id: btId, exclude_employee_id: employeeId, include_empty: true },
      });
      setMoveCandidates(r.data?.bts || []);
    } catch (_e) {
      setMoveCandidates([]);
    } finally {
      setMoveLoading(false);
    }
  };

  const doMove = async () => {
    if (!moveDialog || !moveTargetBt) return;
    setMoving(true);
    try {
      await axios.post(`${API_URL}/api/crm/bons-travail/move-employee-day`, {
        bt_id_source: moveDialog.btId,
        bt_id_target: moveTargetBt,
        employee_id: employeeId,
        date,
      });
      toast.success("Employé déplacé ✓");
      const r = await axios.get(`${API_URL}/api/crm/employe/${employeeId}/horaire-jour`, { params: { date } });
      setData(r.data);
      setMoveDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du déplacement");
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="sm:max-w-lg z-[80]" data-testid="horaire-employe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Calendar className="w-5 h-5 text-blue-500" />
            Horaire de {employeePrenom || data?.employee_nom || 'l\'employé'}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">{date}</p>
        </DialogHeader>
        <div className="py-2 space-y-3 max-h-[60vh] overflow-y-auto">
          {loading && <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin" /></div>}
          {!loading && data && (
            <>
              {data.conge && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm">
                  <span className="font-semibold text-amber-800">🌴 Congé approuvé</span>
                  <span className="text-amber-700 ml-2">({data.conge.type_conge || 'type non précisé'})</span>
                </div>
              )}
              {data.items && data.items.length > 0 ? (
                <ul className="space-y-2">
                  {data.items.map((item, idx) => (
                    <li
                      key={`${item.type}-${item.id}`}
                      className={`rounded-md border p-2.5 text-sm ${item.type === 'SC' ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-200'}`}
                      data-testid={`horaire-item-${item.id}`}
                    >
                      <div className="flex items-start gap-2">
                        {item.type === 'SC'
                          ? <Wrench className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                          : <Briefcase className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800">
                              {item.type === 'SC' ? 'Service Call' : (item.numero_bt || `BT ${idx + 1}`)}
                            </span>
                            {item.ordre_journalier != null && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 rounded px-1.5">
                                Job {item.ordre_journalier + 1}
                              </span>
                            )}
                            {/* Cat H — `item.role` = rôle DANS L'ÉQUIPE DU BT (chef vs membre), pas RBAC utilisateur. */}
                            {item.role === 'chef' && (
                              <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5">👑 Chef</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            <strong>{item.client_nom || item.nom_dossier || '(sans nom)'}</strong>
                            {item.nom_dossier && item.client_nom && item.nom_dossier !== item.client_nom && (
                              <span className="ml-1 text-slate-500">— {item.nom_dossier}</span>
                            )}
                          </div>
                          {item.adresse && (
                            <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3" /> {item.adresse}{item.ville ? `, ${item.ville}` : ''}
                            </div>
                          )}
                          {item.heure_debut && (
                            <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" /> Début prévu : {item.heure_debut}
                            </div>
                          )}
                          {item.chef_equipe_nom && item.role !== 'chef' && (
                            <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <User className="w-3 h-3" /> Chef : {item.chef_equipe_nom}
                            </div>
                          )}
                          {item.description && (
                            <div className="text-[11px] text-slate-500 mt-0.5 italic">{item.description}</div>
                          )}
                          {/* 🆕 2026-05-14 — Boutons Échanger + Déplacer (sans drag) */}
                          {item.type === 'BT' && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[11px] text-blue-700 hover:bg-blue-100"
                                onClick={() => openSwap(item.id, `${item.client_nom || item.numero_bt}`)}
                                data-testid={`btn-swap-${item.id}`}
                              >
                                ↕ Échanger
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[11px] text-emerald-700 hover:bg-emerald-100"
                                onClick={() => openMove(item.id, `${item.client_nom || item.numero_bt}`)}
                                data-testid={`btn-move-${item.id}`}
                              >
                                ↪ Déplacer
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 italic text-center py-3">Aucun BT ni SC assigné ce jour-là.</p>
              )}
              {data.punches && data.punches.length > 0 && (
                <div className="border-t pt-2">
                  <p className="text-xs font-semibold text-slate-600 mb-1">⏱️ Punches réels du jour :</p>
                  <ul className="text-[11px] text-slate-600 space-y-0.5">
                    {data.punches.map(p => (
                      <li key={p.id}>
                        • {(p.punch_in_time || '').slice(11, 16)}
                        {p.punch_out_time ? ` → ${p.punch_out_time.slice(11, 16)}` : ' (en cours)'}
                        {p.contrat_nom ? ` — ${p.contrat_nom}` : ''}
                        {p.heures_nettes != null && ` (${p.heures_nettes.toFixed(2)}h)`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>

      {/* 🆕 Sous-dialog pour choisir l'employé avec qui échanger */}
      <Dialog open={!!swapDialog} onOpenChange={(v) => !v && setSwapDialog(null)}>
        <DialogContent className="sm:max-w-md z-[90]" data-testid="swap-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">↕ Échanger {employeePrenom} sur {swapDialog?.btLabel}</DialogTitle>
            <p className="text-xs text-slate-500 mt-0.5">Choisissez avec qui échanger le {date}</p>
          </DialogHeader>
          <div className="py-2 space-y-2 max-h-[50vh] overflow-y-auto">
            {swapLoading && <div className="flex justify-center py-4"><Loader className="w-4 h-4 animate-spin" /></div>}
            {!swapLoading && swapCandidates.length === 0 && (
              <p className="text-sm text-slate-500 italic">Aucun autre BT actif avec employés ce jour-là.</p>
            )}
            {!swapLoading && swapCandidates.map(bt => (
              <div key={bt.bt_id} className="rounded border border-slate-200 p-2 bg-slate-50">
                <p className="text-xs font-semibold text-slate-700 mb-1">
                  {bt.numero_bt || 'BT'} — {bt.client_nom}
                  {bt.chef_equipe_nom && <span className="ml-1 text-slate-500 font-normal">(Chef : {bt.chef_equipe_nom})</span>}
                </p>
                <div className="space-y-0.5">
                  {bt.employes.map(emp => {
                    const key = `${bt.bt_id}|${emp.id}`;
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-2 text-xs cursor-pointer rounded px-1.5 py-1 ${swapTargetEmp === key ? 'bg-blue-100 text-blue-800' : 'hover:bg-white'}`}
                      >
                        <input
                          type="radio"
                          name="swapTarget"
                          value={key}
                          checked={swapTargetEmp === key}
                          onChange={(e) => setSwapTargetEmp(e.target.value)}
                          data-testid={`swap-radio-${emp.id}`}
                        />
                        <span className="flex-1">{emp.nom_complet}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwapDialog(null)} disabled={swapping}>Annuler</Button>
            <Button
              onClick={doSwap}
              disabled={!swapTargetEmp || swapping}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-confirm-swap"
            >
              {swapping ? <Loader className="w-4 h-4 mr-1 animate-spin" /> : '↕'} Confirmer l'échange
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Sous-dialog DÉPLACER (sans échange) vers un autre BT */}
      <Dialog open={!!moveDialog} onOpenChange={(v) => !v && setMoveDialog(null)}>
        <DialogContent className="sm:max-w-md z-[90]" data-testid="move-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">↪ Déplacer {employeePrenom} depuis {moveDialog?.btLabel}</DialogTitle>
            <p className="text-xs text-slate-500 mt-0.5">Choisissez le BT cible pour le {date} (le BT cible doit déjà avoir cette date dans son chantier)</p>
          </DialogHeader>
          <div className="py-2 space-y-2 max-h-[50vh] overflow-y-auto">
            {moveLoading && <div className="flex justify-center py-4"><Loader className="w-4 h-4 animate-spin" /></div>}
            {!moveLoading && moveCandidates.length === 0 && (
              <p className="text-sm text-slate-500 italic">Aucun autre BT actif ce jour-là.</p>
            )}
            {!moveLoading && moveCandidates.map(bt => (
              <label
                key={bt.bt_id}
                className={`flex items-start gap-2 cursor-pointer rounded border p-2 ${moveTargetBt === bt.bt_id ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
              >
                <input
                  type="radio"
                  name="moveTarget"
                  value={bt.bt_id}
                  checked={moveTargetBt === bt.bt_id}
                  onChange={(e) => setMoveTargetBt(e.target.value)}
                  className="mt-0.5"
                  data-testid={`move-radio-${bt.bt_id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800">
                    {bt.numero_bt || 'BT'} — {bt.client_nom}
                  </p>
                  {bt.chef_equipe_nom && (
                    <p className="text-[11px] text-slate-500">Chef : {bt.chef_equipe_nom}</p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {bt.employes.length === 0
                      ? 'Aucun employé assigné ce jour-là'
                      : `${bt.employes.length} déjà : ${bt.employes.map(e => e.nom_complet).join(', ')}`}
                  </p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog(null)} disabled={moving}>Annuler</Button>
            <Button
              onClick={doMove}
              disabled={!moveTargetBt || moving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-confirm-move"
            >
              {moving ? <Loader className="w-4 h-4 mr-1 animate-spin" /> : '↪'} Confirmer le déplacement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/**
 * Dialog pour ordonner 2+ BT d'un même chef sur une même date.
 * Props :
 *   open, onClose, chefId, chefNom, date, bts (tableau initial — optionnel)
 *   onConfirm(result) — callback après sauvegarde réussie
 */
export function OrdonnerBTsDialog({ open, onClose, chefId, chefNom, date, bts: initialBts, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bts, setBts] = useState(initialBts || []);
  const [ordres, setOrdres] = useState({}); // {bt_id: ordre_int}

  useEffect(() => {
    if (!open) return;
    if (!chefId || !date) return;
    if (initialBts?.length) {
      setBts(initialBts);
      const o = {};
      initialBts.forEach((b, idx) => {
        o[b.id] = b.ordre_journalier != null ? b.ordre_journalier : idx;
      });
      setOrdres(o);
      return;
    }
    // Sinon, fetch via /collisions
    setLoading(true);
    axios.get(`${API_URL}/api/crm/bons-travail/collisions`, { params: { chef_id: chefId, date } })
      .then(res => {
        const list = res.data?.bons_travail || [];
        setBts(list);
        const o = {};
        list.forEach((b, idx) => {
          o[b.id] = b.ordre_journalier != null ? b.ordre_journalier : idx;
        });
        setOrdres(o);
      })
      .catch(() => toast.error('Erreur chargement collisions'))
      .finally(() => setLoading(false));
  }, [open, chefId, date, initialBts]);

  const allOrdres = Object.values(ordres);
  const hasDuplicates = new Set(allOrdres).size !== allOrdres.length;

  const handleSave = async () => {
    if (hasDuplicates) {
      toast.error('Chaque BT doit avoir un ordre différent');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date,
        chef_id: chefId,
        ordres: Object.entries(ordres).map(([bt_id, ordre]) => ({
          bon_travail_id: bt_id,
          ordre: Number(ordre),
        })),
      };
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/set-ordre-journalier`, payload);
      const data = res.data;
      if (data?.status === 'success') {
        toast.success('Ordre enregistré');
        onConfirm && onConfirm(data);
        onClose && onClose();
      } else {
        toast.error('Erreur');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur';
      toast.error(`Erreur : ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="sm:max-w-lg w-[calc(100%-2rem)] max-h-[85vh] overflow-y-auto z-[60]"
        data-testid="ordonner-bts-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg pr-6">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <span className="break-words">Ordonner les BT/SC du {date}</span>
          </DialogTitle>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {chefNom ? `Chef : ${chefNom}` : 'Choisir l\'ordre de réalisation'}
          </p>
        </DialogHeader>
        <div className="py-3">
          {loading ? (
            <div className="flex justify-center py-8"><Loader className="w-5 h-5 animate-spin" /></div>
          ) : bts.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Aucun BT trouvé.</p>
          ) : (
            <ul className="space-y-2">
              {bts.map((b) => (
                <li
                  key={b.id}
                  className={`flex items-center justify-between gap-2 rounded-md border p-2 text-sm w-full ${b._is_service_call ? 'bg-orange-50 border-orange-200' : 'bg-white'}`}
                  data-testid={`ordonner-bt-item-${b.id}`}
                >
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="font-medium text-slate-700 truncate text-xs sm:text-sm" data-testid={`ordonner-bt-titre-${b.id}`}>
                      {b._is_service_call
                        ? <span className="inline-flex items-center gap-1 text-orange-700">🔧 Service Call</span>
                        : <>{b.numero_bt || 'BT'}</>}
                      {' '}— {b.client_nom || b.nom_dossier || stripHtml(b.description_travaux, { maxLen: 60 }) || '(Sans nom)'}
                    </p>
                    {b.heure_debut && (
                      <p className="text-[10px] sm:text-xs text-slate-500 truncate">Heure début prévue : {b.heure_debut}</p>
                    )}
                  </div>
                  <Select
                    value={String(ordres[b.id] ?? '')}
                    onValueChange={(v) => setOrdres(prev => ({ ...prev, [b.id]: Number(v) }))}
                  >
                    <SelectTrigger className="w-24 flex-shrink-0" data-testid={`select-ordre-${b.id}`}>
                      <SelectValue placeholder="Ordre" />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      {bts.map((_, idx) => (
                        <SelectItem key={idx} value={String(idx)}>
                          Job {idx + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </li>
              ))}
            </ul>
          )}
          {hasDuplicates && !loading && (
            <p className="text-xs text-red-600 mt-2">⚠️ Deux BT ne peuvent pas avoir le même ordre.</p>
          )}
        </div>
        <DialogFooter className="gap-2 flex-row justify-end sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={saving} className="flex-shrink-0">Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading || bts.length === 0 || hasDuplicates}
            data-testid="btn-ordonner-save"
            className="flex-shrink-0"
          >
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : 'Enregistrer l\'ordre'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Bannière inline à insérer dans les dialogs d'édition BT.
 * Fait le fetch /collisions au changement de chef/date et affiche une alerte.
 * Props : chefId, chefNom, date, currentBtId (pour exclure le BT en cours), disabled
 *         employeeIds (🆕 2026-05-14 — liste d'IDs d'employés assignés au BT courant
 *                      pour ne déclencher l'alerte que si conflit RÉEL d'employé).
 */
export function CollisionChecker({ chefId, chefNom, date, currentBtId, disabled, employeeIds }) {
  const [loading, setLoading] = useState(false);
  const [others, setOthers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  // 🆕 2026-05-14 — Popup d'horaire d'un employé spécifique (clic sur prénom)
  const [horaireEmp, setHoraireEmp] = useState(null); // {id, prenom}

  useEffect(() => {
    if (disabled || !chefId || !date) {
      setOthers([]);
      return;
    }
    const handler = setTimeout(() => {
      setLoading(true);
      const empParam = Array.isArray(employeeIds) && employeeIds.length > 0
        ? employeeIds.join(',')
        : null;
      axios.get(`${API_URL}/api/crm/bons-travail/collisions`, {
        params: {
          chef_id: chefId,
          date,
          ...(currentBtId ? { exclude_bt_id: currentBtId } : {}),
          ...(empParam ? { employee_ids: empParam } : {}),
        }
      })
        .then(res => setOthers(res.data?.bons_travail || []))
        .catch(() => setOthers([]))
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(handler);
  }, [chefId, date, currentBtId, disabled, employeeIds]);

  if (disabled) return null;
  if (loading) return null;
  if (!others.length) return null;

  // Prépare la liste pour le dialog (inclut le BT courant si existant)
  const btsForDialog = currentBtId
    ? [...others, { id: currentBtId, numero_bt: 'Ce BT', description_travaux: '(BT en cours d\'édition)' }]
    : others;

  return (
    <>
      <div
        className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm flex items-start gap-2"
        data-testid="collision-checker-alert"
      >
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          {(() => {
            // 🆕 2026-05-18 — Titre plus précis : le chef principal n'est mentionné
            //   QUE s'il est lui-même en conflit (présent dans _conflicting_employees
            //   d'un des autres BT). Sinon le titre cite les vrais employés en conflit.
            const chefEnConflit = others.some(b =>
              (b._conflicting_employees || []).some(e => e.id === chefId)
            );
            const empsConflitDistincts = Array.from(
              new Set(
                others.flatMap(b =>
                  (b._conflicting_employees || [])
                    .filter(e => e.id !== chefId)
                    .map(e => e.prenom)
                )
              )
            ).filter(Boolean);
            let titreSujet;
            if (chefEnConflit) {
              titreSujet = chefNom || 'ce chef';
            } else if (empsConflitDistincts.length > 0) {
              titreSujet = empsConflitDistincts.slice(0, 3).join(', ') +
                (empsConflitDistincts.length > 3 ? ` et ${empsConflitDistincts.length - 3} autre(s)` : '');
            } else {
              // Pas d'employés explicites en conflit → fallback neutre
              titreSujet = "l'équipe";
            }
            return (
              <p className="font-semibold text-amber-800">
                ⚠️ Conflit d'horaire détecté pour {titreSujet} le {date} — {others.length} autre{others.length > 1 ? 's' : ''} chantier{others.length > 1 ? 's' : ''}/SC :
              </p>
            );
          })()}
          <ul className="text-xs text-amber-700 mt-1.5 space-y-1.5">
            {others.map((b) => {
              const conflictPrenoms = (b._conflicting_employees || []).map(e => e.prenom).filter(Boolean);
              const titreClient = b.client_nom || b.nom_dossier || stripHtml(b.description_travaux, { maxLen: 80 }) || '(sans nom)';
              const typeLabel = b._is_service_call ? '🔧 Service Call' : (b.numero_bt || 'BT');
              return (
                <li key={b.id} className="border-l-2 border-amber-300 pl-2 py-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{typeLabel}</span>
                    <span>— {titreClient}</span>
                    {b.ordre_journalier != null && (
                      <span className="inline-flex text-[10px] bg-blue-100 text-blue-700 rounded px-1">
                        Job {b.ordre_journalier + 1}
                      </span>
                    )}
                  </div>
                  {/* 🆕 2026-05-14 — Détails enrichis : heure, chef alternatif, employés en conflit */}
                  <div className="text-[11px] text-amber-700/80 mt-0.5 flex items-center gap-2 flex-wrap">
                    {b.heure_debut && <span>⏰ {b.heure_debut}</span>}
                    {b.chef_equipe_nom && b.chef_equipe_nom !== chefNom && <span>👤 Chef : {b.chef_equipe_nom}</span>}
                    {conflictPrenoms.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                        ⚠️ Employé{conflictPrenoms.length > 1 ? 's' : ''} en double :{' '}
                        {(b._conflicting_employees || []).map((emp, i) => (
                          <span key={emp.id}>
                            <button
                              type="button"
                              onClick={() => setHoraireEmp({ id: emp.id, prenom: emp.prenom })}
                              className="underline hover:text-red-900 font-semibold cursor-pointer"
                              data-testid={`btn-voir-horaire-${emp.id}`}
                              title={`Voir l'horaire complet de ${emp.prenom} le ${date}`}
                            >
                              {emp.prenom}
                            </button>
                            {i < (b._conflicting_employees.length - 1) ? ', ' : ''}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-amber-700/70 mt-2 italic">
            💡 Si tu ordonnes les BT/SC, l'ordre s'applique à toute l'équipe. Pour un ordre par employé (Stéphanie commence ici, Jacob commence là), choisis explicitement chaque job pour chaque personne via "Choisir l'ordre".
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
            onClick={() => setDialogOpen(true)}
            data-testid="btn-open-ordonner"
          >
            Choisir l'ordre
          </Button>
        </div>
      </div>
      <OrdonnerBTsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        chefId={chefId}
        chefNom={chefNom}
        date={date}
        bts={btsForDialog}
        onConfirm={() => {}}
      />
      {/* 🆕 2026-05-14 — Popup horaire détaillé au clic sur un prénom en conflit */}
      <HoraireEmployeJourDialog
        open={!!horaireEmp}
        onClose={() => setHoraireEmp(null)}
        employeeId={horaireEmp?.id}
        employeePrenom={horaireEmp?.prenom}
        date={date}
      />
    </>
  );
}
